import { ErrorHandler } from '@angular/core';
import type { Breadcrumb } from '@sentry/angular';
import { environment } from '@environments/environment';

type SentryModule = typeof import('./sentry-sdk');

/** The object form of `captureMessage`'s context: the one that carries tags. */
type MessageContext = Extract<
  NonNullable<Parameters<SentryModule['captureMessage']>[1]>,
  { tags?: unknown }
>;

/**
 * Share of ordinary errors that reach Sentry. Generic runtime errors are high
 * volume and repetitive, so a small sample is enough to spot a regression.
 *
 * Applied per *session* rather than per event: the SDK used to be downloaded
 * on every load only to discard 99% of what it captured. Deciding once, before
 * bootstrap, keeps the same reporting volume and costs the other 99% of loads
 * nothing at all.
 */
const SESSION_SAMPLE_RATE = 0.01;

/** Overall share of page loads traced, expressed as a rate within sampled sessions. */
const TRACES_SAMPLE_RATE = 0.0005 / SESSION_SAMPLE_RATE;

/**
 * Breadcrumbs passed to `addBreadcrumb` before the SDK exists, kept so an
 * on-demand report still carries the scanner's trace. The SDK's automatic
 * breadcrumbs start only at `init`. Bounded like the SDK's own buffer.
 */
const MAX_PENDING_BREADCRUMBS = 100;

/**
 * Errors raised outside Angular before the SDK installs its own handlers.
 * Bounded so an error thrown in a loop cannot grow the queue while it loads.
 */
const MAX_EARLY_ERRORS = 100;

/**
 * ZXing logs this once per failed decode attempt — several times a second while
 * the scanner runs. Captured as console breadcrumbs they fill the 100-entry
 * buffer within seconds, pushing out the scanner's own trail (which camera was
 * opened, at what resolution, how far playback got) that a WebView-only bug can
 * only be diagnosed from.
 */
const ZXING_DECODE_NOISE = 'MultiFormatReader: non-ReaderException';

const DSN =
  'https://6d3fde4f683e7c087c3cf62e378c51bc@o233914.ingest.us.sentry.io/4509921492926464';

let sentry: Promise<SentryModule> | null = null;
let sessionSampled = false;
const pendingBreadcrumbs: Breadcrumb[] = [];
const earlyErrors: { error: unknown; mechanismType: string }[] = [];

// The mechanism Sentry's own handlers attach to what nobody caught. Without it
// an event counts as handled, and drops out of the "Unhandled" filter and of
// the alerts built on `error.handled:no`.
const unhandled = (type: string) => ({ mechanism: { type, handled: false } });

function load(): Promise<SentryModule> {
  sentry ??= import('./sentry-sdk')
    .then((module: SentryModule) => {
      module.init({
        dsn: DSN,
        environment: environment.retailerName,
        // Setting this option to true will send default PII data to Sentry.
        // For example, automatic IP address collection on events
        sendDefaultPii: false,
        // Events tagging themselves `alwaysSend` are the first sighting of a
        // given failure, often the only evidence of a WebView-only bug, and must
        // not be lost to a 1% dice roll: they load the SDK on demand in any
        // session. A session loaded only for one of them must not then start
        // reporting its ordinary errors, or the volume would drift above the
        // sample. Whoever sets the tag owns bounding how often it is set — see
        // `ScannerDiagnosticsService.RARE_STAGES` and `HIGH_VOLUME_SAMPLE_RATE`.
        beforeSend: (event) =>
          event.tags?.['alwaysSend'] || sessionSampled ? event : null,
        beforeBreadcrumb: (breadcrumb) =>
          breadcrumb.category === 'console' &&
          breadcrumb.message?.startsWith(ZXING_DECODE_NOISE)
            ? null
            : breadcrumb,
        integrations: sessionSampled
          ? [module.browserTracingIntegration()]
          : [],
        tracesSampleRate: sessionSampled ? TRACES_SAMPLE_RATE : 0,
      });
      module.setTag('retailer', environment.retailerName);
      for (const breadcrumb of pendingBreadcrumbs.splice(0)) {
        module.addBreadcrumb(breadcrumb);
      }
      for (const { error, mechanismType } of earlyErrors.splice(0)) {
        module.captureException(error, unhandled(mechanismType));
      }
      return module;
    })
    .catch((error: unknown) => {
      // A deploy removed the chunk, or the network dropped. Forget this
      // attempt so the next report loads the SDK again.
      sentry = null;
      throw error;
    });
  return sentry;
}

// The failed load has reset itself; the next report tries again.
const ignoreFailedLoad = (): void => undefined;

function withSdk(use: (module: SentryModule) => void): void {
  void load().then(use, ignoreFailedLoad);
}

function queueEarlyError(event: ErrorEvent | PromiseRejectionEvent): void {
  if (earlyErrors.length < MAX_EARLY_ERRORS) {
    earlyErrors.push(
      'reason' in event
        ? {
            error: event.reason,
            mechanismType: 'auto.browser.global_handlers.onunhandledrejection',
          }
        : {
            error: event.error ?? event.message,
            mechanismType: 'auto.browser.global_handlers.onerror',
          }
    );
  }
}

function stopQueueingEarlyErrors(): void {
  window.removeEventListener('error', queueEarlyError);
  window.removeEventListener('unhandledrejection', queueEarlyError);
}

/**
 * Decide once per session whether this load reports ordinary errors, and start
 * fetching the SDK when it does. Must run before bootstrap so the fetch
 * overlaps with it.
 */
export function startSentry(): void {
  if (!environment.enableSentry) {
    return;
  }

  sessionSampled = Math.random() < SESSION_SAMPLE_RATE;
  if (sessionSampled) {
    // The SDK installs its own handlers only once loaded, and it loads during
    // bootstrap: queue the errors raised outside Angular until then.
    window.addEventListener('error', queueEarlyError);
    window.addEventListener('unhandledrejection', queueEarlyError);
    void load().catch(ignoreFailedLoad).finally(stopQueueingEarlyErrors);
  }
}

export function addBreadcrumb(breadcrumb: Breadcrumb): void {
  if (!environment.enableSentry) {
    return;
  }

  if (sentry) {
    void sentry.then(
      (module: SentryModule) => module.addBreadcrumb(breadcrumb),
      () => queueBreadcrumb(breadcrumb)
    );
    return;
  }

  queueBreadcrumb(breadcrumb);
}

function queueBreadcrumb(breadcrumb: Breadcrumb): void {
  pendingBreadcrumbs.push(breadcrumb);
  if (pendingBreadcrumbs.length > MAX_PENDING_BREADCRUMBS) {
    pendingBreadcrumbs.shift();
  }
}

/**
 * Report a message. Sampled like an error unless its tags carry `alwaysSend`,
 * which fetches the SDK if this session has not already. Resolves to whether
 * the SDK received it: false when it was skipped, the SDK failed to load, or
 * the SDK threw.
 */
export function captureMessage(
  message: string,
  context: MessageContext
): Promise<boolean> {
  if (!environment.enableSentry) {
    return Promise.resolve(false);
  }

  const alwaysSend = Boolean(context.tags?.['alwaysSend']);
  if (!alwaysSend && !sessionSampled) {
    return Promise.resolve(false);
  }

  return load()
    .then((module: SentryModule) => {
      module.captureMessage(message, context);
      return true;
    })
    .catch(() => false);
}

/**
 * Report an error when this session is one of the sampled ones. Errors thrown
 * before the SDK finishes loading still arrive: the promise resolves first.
 */
function reportError(error: unknown): void {
  if (sessionSampled) {
    withSdk((module: SentryModule) =>
      module.captureException(
        error,
        unhandled('auto.function.angular.error_handler')
      )
    );
  }
}

export class SentryErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    reportError(error);
    console.error(error);
  }
}
